// System Architecture & Design Scenarios — vocabulary (Phase B;
// docs/implementation/SYSTEM_ARCHITECTURE_AND_SCENARIOS.md). The registry is
// the extensible type / strategy model the brief asks for: adding an
// architecture is a new entry here (the DB stores the key and does not
// enumerate them). HVAC first; other domains later.
//
//   plantCategories   which ProcessEquipment.Category values count as this
//                     architecture's plant asset (the picker filters on them)
//   distribution      the distribution family a Phase E / F route will use
//   needsDeltaT       chilled-water GPM = BTUH ÷ (500 × ΔT) needs an EXPLICIT
//                     project ΔT — never a hidden default (Phase C)
//   consequences      the cross-discipline scope the brief's Phase H expects
//                     the scenario to propagate (documentation for now)

export const SCENARIO_DOMAINS = [{ key: "hvac", label: "HVAC", prefix: "HVAC" }];
export const DEFAULT_SCENARIO_DOMAIN = "hvac";

export const SYSTEM_ARCHITECTURES = [
  {
    key: "dx", label: "Packaged / split DX", domain: "hvac", distribution: "refrigerant", needsDeltaT: false,
    plantCategories: ["dx_unit", "condensing_unit"], distributionTypes: ["refrigerant_split", "refrigerant_vrf", "packaged_rooftop"],
    description: "Direct-expansion cooling — condensing units or packaged units serve the AHU / DX coils; refrigerant piping, distributed electrical loads, roof / yard equipment.",
    consequences: ["condensing units", "refrigerant piping", "roof / yard equipment and supports", "distributed electrical loads", "controls", "refrigerant-specific engineering / installation"],
  },
  {
    key: "chilled_water", label: "Central chilled water", domain: "hvac", distribution: "chilled_water", needsDeltaT: true,
    plantCategories: ["chiller", "pump_package"], distributionTypes: ["chw_2pipe", "chw_4pipe"],
    description: "Central chiller plant serving AHU chilled-water coils through CHWS / CHWR mains and branches; pumps, plant accessories, plant electrical loads.",
    consequences: ["chillers", "pumps", "CHW piping", "valves / accessories", "plant controls", "mechanical room / yard", "plant electrical loads", "freight / rigging", "engineering hours", "procurement duration"],
  },
];
export const ARCHITECTURE_BY_KEY = Object.fromEntries(SYSTEM_ARCHITECTURES.map(a => [a.key, a]));
export const architectureLabel = (key) => ARCHITECTURE_BY_KEY[key]?.label || key || "—";

// Phase F — field refrigerant lines per DX distribution type (conceptual: one
// line SET per condensing unit; sizing is manufacturer / tonnage specific and
// is NEVER fabricated here — every run reads Sizing Required until the
// selected equipment's line sizes are entered as overrides).
export const REFRIGERANT_LINES = {
  refrigerant_split: [{ key: "liquid", label: "Liquid line" }, { key: "suction", label: "Suction line" }],
  refrigerant_vrf:   [{ key: "liquid", label: "Liquid line" }, { key: "suction", label: "Suction line" }], // 2-pipe VRF; heat-recovery 3-pipe is a later distribution type
  packaged_rooftop:  [], // factory-charged; no field refrigerant piping
};
export const DEFAULT_DX_DISTRIBUTION = "refrigerant_split";
export const refrigerantLinesFor = (distributionType) => REFRIGERANT_LINES[distributionType || DEFAULT_DX_DISTRIBUTION] ?? REFRIGERANT_LINES[DEFAULT_DX_DISTRIBUTION];

export const DISTRIBUTION_TYPE_LABELS = {
  refrigerant_split: "Refrigerant — split systems", refrigerant_vrf: "Refrigerant — VRF", packaged_rooftop: "Packaged rooftop (no field refrigerant piping)",
  chw_2pipe: "Chilled water — 2-pipe", chw_4pipe: "Chilled / heating water — 4-pipe",
};
export const distributionTypeLabel = (key) => DISTRIBUTION_TYPE_LABELS[key] || key || "—";

export const SCENARIO_STATUSES = [
  { key: "draft",              label: "Draft",              description: "Being set up; not yet compared." },
  { key: "active_comparison",  label: "Active comparison",  description: "Reviewed alongside the others; not the base." },
  { key: "selected_base",      label: "Selected base",      description: "The base design for this domain — one per revision. Estimate, schedules and proposal consume it (Phase J)." },
  { key: "proposal_alternate", label: "Proposal alternate", description: "Published as an additive / deductive alternate to the base." },
  { key: "archived",           label: "Archived",           description: "Kept for the record; not compared." },
];
export const SCENARIO_STATUS_KEYS = SCENARIO_STATUSES.map(s => s.key);
export const scenarioStatusLabel = (key) => SCENARIO_STATUSES.find(s => s.key === key)?.label || key || "—";
export const COMPARED_STATUSES = new Set(["active_comparison", "selected_base", "proposal_alternate"]);
